<script lang="ts">
  import { Button } from '@athena-lib/Button';
  import { TextField } from '@athena-lib/Fields';
  import { HeadingSmall, Text } from '@athena-lib/Text';
  import type { LoginFormHandler } from '@athena-lib/LoginForm';
  import { getCommonTranslations } from '@athena-lib/commonTranslationsContext';
  import { onMount } from 'svelte';
  import { required, handler } from './utils';
  import Notifications from './Notifications.svelte';
  import { type LoginFormTrackingHandler } from './types';

  type Props = {
    onCreateAccount: LoginFormHandler;
    goToLogin: () => void;
    onFormTracking: LoginFormTrackingHandler;
  };

  let { onCreateAccount, goToLogin, onFormTracking }: Props = $props();

  let email = $state('');
  let password = $state('');
  let confirmPassword = $state('');
  let confirmError = $state<string | undefined>(undefined);
  let errors = $state<string[] | undefined>(undefined);
  let messages = $state<string[] | undefined>(undefined);

  let loginFormElement: HTMLFormElement | undefined = undefined;

  const canSubmit = $derived(email && password && confirmPassword);

  const translations = getCommonTranslations();

  const handleCreateAccount = handler(onCreateAccount, {
    defaultMessage: translations.generalError,
    onSuccess: (n) => {
      onFormTracking('submit', 'signup', true);
      messages = n;
    },
    onError: (e) => {
      onFormTracking('error', 'signup', true);
      errors = e;
    },
    reset: () => {
      errors = undefined;
      messages = undefined;
    },
    guard: () => {
      if (password !== confirmPassword) {
        confirmError = translations.confirmPasswordError;
        return false;
      }

      return true;
    },
  });

  const resetConfirmError = () => {
    if (!confirmError) {
      return;
    }

    confirmError = undefined;
  };

  onMount(() => {
    onFormTracking('show', 'signup', false);
  });

  const maybeTriggerCompleteEvent = () => {
    if (!loginFormElement?.checkValidity()) {
      return;
    }

    if (password !== confirmPassword) {
      return;
    }

    onFormTracking('complete', 'signup', false);
  };
</script>

<div class="flex flex-col">
  <div class="text-center">
    <HeadingSmall size="small">{translations.checkoutLoginFormCreateAccountTitle}</HeadingSmall>
    <Text element="span">
      {translations.checkoutLoginFormCreateAccountDescription}
    </Text>
  </div>
  {#if errors || messages}
    <Notifications {errors} {messages} />
  {/if}
  <form
    bind:this={loginFormElement}
    onsubmit={handleCreateAccount}
    class="mt-8 flex flex-col gap-4 data-[has-notifications=true]:mt-0"
    data-has-notifications={!!errors || !!messages}
  >
    <TextField
      type="email"
      name="users[credentials][email]"
      label={required(translations.email)}
      bind:value={email}
      required
      oninput={() => {
        onFormTracking('start', 'signup', false);
        maybeTriggerCompleteEvent();
      }}
    />
    <TextField
      type="password"
      name="users[credentials][password_field_group][custom_new_password]"
      label={required(translations.password)}
      bind:value={password}
      required
      oninput={() => {
        onFormTracking('start', 'signup', false);
        maybeTriggerCompleteEvent();
      }}
    />
    <TextField
      type="password"
      name="users[credentials][password_field_group][custom_new_password_confirm]"
      label={required(translations.confirmPassword)}
      bind:value={confirmPassword}
      error={confirmError}
      onkeydown={resetConfirmError}
      required
      oninput={() => {
        onFormTracking('start', 'signup', false);
        maybeTriggerCompleteEvent();
      }}
    />
    <div class="mt-4 flex justify-center">
      <Button type="submit" class="max-w-fit" disabled={!canSubmit} wide>
        {translations.createAccount}
      </Button>
    </div>
  </form>
  <div class="mt-8 flex items-center justify-center gap-2">
    <Text>
      {translations.haveAnAccount}
    </Text>
    <button type="button" onclick={goToLogin}>
      <Text element="span" class="underline">{translations.signIn}</Text>
    </button>
  </div>
</div>
